import { Component, OnDestroy, OnInit } from '@angular/core';
import { LetterData, ScheduleALetterSearchParameters } from './scheduleA-page.types';
import { BehaviorSubject, Subscription, combineLatest } from 'rxjs';
import { DataTableHeaderOption, ErrorControl, createErrorControlMap } from '@dxo/ng-swift-ui';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  UntypedFormArray,
  UntypedFormControl,
  UntypedFormGroup,
  Validators
} from '@angular/forms';
import { dateFromValidator, dateToValidator, dateValidator } from '@benefitsui/app/shared/validators/dateValidation';
import { distinctUntilChanged, map, switchMap, tap } from 'rxjs/operators';
import { HttpErrorService } from '@benefitsui/app/services/http-error/http-error.service';
import {
  FormsClient,
  ScheduleALetterSearchReponse,
  SearchSortField,
  SearchSortDirection
} from '@benefitsui/app/api/benefits-clients';
import { PolicyService } from '@benefitsui/app/services/policy/policy.service';
import { Constants } from '../billing-page/billing-page.types';

@Component({
  selector: 'app-schedulea-page',
  templateUrl: './scheduleA-page.component.html',
  styleUrls: ['./scheduleA-page.component.scss']
})
export class ScheduleAPageComponent implements OnInit, OnDestroy {
  constructor(
    private formsClient: FormsClient,
    private errorService: HttpErrorService,
    private policyService: PolicyService,
    private fb: FormBuilder
  ) {
    this.formGroup = this.fb.group({
      fromDate: [Constants.StringEmpty, [Validators.required, dateValidator, dateFromValidator]],
      toDate: [Constants.StringEmpty, [Validators.required, dateValidator, dateToValidator]]
    });

    this.formErrors = {
      ...this.getErrorMap(this.formGroup)
    };
  }

  // :  Data members
  formErrors: { [key: string]: ErrorControl };
  formGroup: FormGroup;

  hasServerError = false;
  messageHeader = 'Tax Forms are currently unavailable';
  messageBody = 'We apologize for the system not operating as expected and are working to fix it.';
  isLoading = true;
  private pageSize$ = new BehaviorSubject<number>(10);
  private page$ = new BehaviorSubject<number>(1);
  private sortDirection$ = new BehaviorSubject<SearchSortDirection>(SearchSortDirection.Descending);
  private sortField$ = new BehaviorSubject<SearchSortField>(SearchSortField.StatementDate);

  searchParameter: ScheduleALetterSearchParameters = {
    page: 1,
    pageSize: 10,
    sortField: SearchSortField.StatementDate,
    sortDirection: SearchSortDirection.Descending
  };
  totalLines = 0;
  subscriptions: Subscription[] = [];
  // eslint-disable-next-line
  dateCharRegex = /[0-9\-\/]/;

  ngOnInit(): void {
    this.subscriptions.push(
      this.formsClient
        .scheduleALetter({
          sortDirection: this.searchParameter.sortDirection,
          page: this.searchParameter.page,
          pageSize: this.searchParameter.pageSize,
          sortField: this.searchParameter.sortField,
          policyId: undefined,
          policyIds: undefined
        })
        .subscribe(
          (x) => {
            this.totalLines = x.data?.totalPages ?? 0;
            if (this.totalLines === 0) {
              this.isLoading = false;
            }
          },
          () => {
            this.errorService.preventNextErrorRedirect();
            this.hasServerError = true;
            this.isLoading = false;
          },
          () => {
            this.isLoading = false;
          }
        ),
      this.formGroup.controls.fromDate.valueChanges.subscribe((v) =>
        this.formGroup.controls.fromDate.setValue(this.formatDateWhenTyping(v), { emitEvent: false })
      ),
      this.formGroup.controls.toDate.valueChanges.subscribe((v) =>
        this.formGroup.controls.toDate.setValue(this.formatDateWhenTyping(v), { emitEvent: false })
      )
    );
  }

  letters$ = combineLatest([
    this.page$.pipe(distinctUntilChanged()),
    this.pageSize$.pipe(distinctUntilChanged()),
    this.sortDirection$.pipe(distinctUntilChanged()),
    this.sortField$.pipe(distinctUntilChanged())
  ])
    .pipe(
      switchMap((vals) => {
        this.searchParameter.page = vals[0];
        this.searchParameter.pageSize = vals[1];
        this.searchParameter.sortDirection = vals[2];
        this.searchParameter.sortField = vals[3];

        return this.formsClient
          .scheduleALetter({
            sortDirection: this.searchParameter.sortDirection,
            page: this.searchParameter.page,
            pageSize: this.searchParameter.pageSize,
            sortField: this.searchParameter.sortField,
            policyId: undefined,
            policyIds: undefined
          })
          .pipe(map((x) => x.data));
      })
    )
    .pipe(
      tap((x) => {
        (this.isLoading = false), (this.totalLines = x?.totalResults ?? 0);
      }),
      map((x) => this.transformData(x))
    );

  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => {
      s?.unsubscribe();
    });
  }

  formInit() {
    this.formGroup = this.fb.group({
      fromDate: [Constants.StringEmpty, [Validators.required, dateValidator, dateFromValidator]],
      toDate: [Constants.StringEmpty, [Validators.required, dateValidator, dateToValidator]]
    });
  }

  getErrorMap = (formGroup: FormGroup) =>
    createErrorControlMap(formGroup, {
      fromDate: {
        ...{
          required: 'This is a required field.',
          dateFormat: 'The date must be in a MM/DD/YYYY format.',
          checkYears: 'From or To Date is older than seven years.',
          checkDay: 'The date must be in the past.'
        }
      },
      toDate: {
        ...{
          required: 'This is a required field.',
          dateFormat: 'The date must be in a MM/DD/YYYY format.',
          dateCompare: 'The from date must occur before the to date.',
          checkYears: 'From or To Date is older than seven years.',
          checkDay: 'The date must be in the past.'
        }
      }
    }) as object as {
      fromDate: ErrorControl;
      toDate: ErrorControl;
    };

  transformData(response: ScheduleALetterSearchReponse | undefined): LetterData[] {
    const returnTableCells: LetterData[] = [];
    response?.letters?.map((letter) => {
      returnTableCells.push({
        statementDate: letter.statementDate ?? Constants.StringEmpty,
        planYear: letter.planYear ?? Constants.StringEmpty,
        pdfUrl: letter.fileUri ?? Constants.StringEmpty,
        pdfId: letter.fileId ?? Constants.StringEmpty
      });
    });

    return returnTableCells ?? [];
  }

  tableHeaders: DataTableHeaderOption[] = [
    { text: 'Year of policy or contract', key: 'planYear', width: '65%' },
    { text: 'Actions', key: 'pdfUrl' }
  ];

  updateControls(control: AbstractControl) {
    if (control instanceof UntypedFormControl) {
      control.updateValueAndValidity();
    } else if (control instanceof UntypedFormGroup) {
      Object.values(control.controls).forEach((childControl) => this.updateControls(childControl));
    } else if (control instanceof UntypedFormArray) {
      control.updateValueAndValidity();
      control.controls.forEach((childControl) => this.updateControls(childControl));
    }
  }

  onPageChange(page: number) {
    this.page$.next(page);
  }

  onPageSizeChange(pageSize: number) {
    this.pageSize$.next(pageSize);
  }

  onClickTableHeader(sortField: string) {
    let newSortDirection = SearchSortDirection.Descending;
    if (this.searchParameter.sortDirection === SearchSortDirection.Descending) {
      newSortDirection = SearchSortDirection.Ascending;
    }
    const field: SearchSortField = SearchSortField[sortField as keyof typeof SearchSortField];
    this.sortDirection$.next(newSortDirection);
    this.sortField$.next(field);
  }

  open = false;
  modalSuccess = false;
  async submit() {
    this.request();
  }

  request() {
    if (this.formGroup.invalid) {
      this.updateControls(this.formGroup);
      return;
    }
    this.open = true;
    this.formsClient
      .requestScheduleALetter({
        dateFrom: this.formGroup.controls.fromDate.value!,
        dateTo: this.formGroup.controls.toDate.value!,
        policyId: this.policyService.policyNumber,
        policyName: this.policyService.policyName,
        requestorUserName: undefined,
        requestorName: undefined,
        requestorEmail: undefined
      })
      .subscribe((response) => {
        if (response.messages && response.messages?.length > 0) {
          console.log('Something wrong');
        } else {
          this.formGroup.markAsPristine();
          this.modalSuccess = true;
        }
      });
  }
  closePopup() {
    this.open = false;
  }

  generateFileName(item: string): string {
    return 'ScheduleALetter-' + item;
  }

  private formatDateWhenTyping(value: string | null) {
    if (value === null) {
      return Constants.StringEmpty;
    }
    value = value.split('-').join('/');
    const parts = value.split('/');
    for (let i = 0; i < parts.length - 1; i++) {
      parts[i] = parts[i].padStart(2, '0');
    }

    value = parts.join('/');
    if (value.length > 2 && !isNaN(parseInt(value[2], 10))) {
      value = value.slice(0, 2) + '/' + value[2];
    }
    if (value.length > 5 && !isNaN(parseInt(value[5], 10))) {
      value = value.slice(0, 5) + '/' + value[5];
    }
    return value;
  }
}
